import React, { FC, useMemo } from 'react';
import { FaBullhorn, FaCreditCard, FaRegFileAlt } from 'react-icons/fa';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { Button, TabItem, Tabs } from '@/lib';
import { RouteConstants } from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetCampaignsCountV2Query } from '@/services/endpoints/campaigns';
import { useWhatsappTemplates } from '../hooks/useWhatsappTemplates';
import { CampaignsWhatsappRoutesV2 } from '../Whatsapp/CampaignsWhatsappRoutesV2';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { useCompanyValues } from '@/providers';
import { WHATSAPP_CONNECTOR_NAME } from '../../components/CreateCampaign_v2/steps/Whatsapp/constants';
import { useTranslation } from 'react-i18next';
import { Loading } from '@/components/loaders';

const WHATSAPP_CAMPAIGNS_PATTERNS = [
  RouteConstants.whatsappCampaigns,
  RouteConstants.createCampaignWhatsapp,
  RouteConstants.editCampaignWhatsapp,
  RouteConstants.whatsappCampaignDetails,
];

const WHATSAPP_TEMPLATES_PATTERNS = [
  RouteConstants.whatsappTemplates,
  RouteConstants.createWhatsappTemplate,
  RouteConstants.editWhatsappTemplate,
  RouteConstants.whatsappTemplateDetails,
];

const WHATSAPP_PACKAGES_PATTERNS = [RouteConstants.whatsappPackages];

const ConnectorNotConnectedBlock: FC<{ type: 'whatsapp' }> = ({ type }) => {
  const history = useHistory();
  const { t } = useTranslation('campaign');

  const handleConnect = () => {
    if (type === 'whatsapp') {
      history.push(RouteConstants.destinations);
    }
  };

  return (
    <div className='flex flex-col items-center justify-center gap-6 py-20 border border-gray-400 rounded-lg'>
      <h2 className='text-4xl text-slate-600 font-semibold'>{t('whatsappWorkspace.noWhatsappConnectorTitle', { connectorName: WHATSAPP_CONNECTOR_NAME })}</h2>
      <div className='flex flex-col items-center gap-4'>
        {t('whatsappWorkspace.noWhatsappConnectorDescription', { connectorName: WHATSAPP_CONNECTOR_NAME })}
      </div>
      <FaRegFileAlt className='size-12 text-gray-400' />
      <Button onClick={handleConnect}>{t('goToDestinationsText')}</Button>
    </div>
  );
};

const WhatsappWorkspaceInit = () => {
  const history = useHistory();
  const location = useLocation();
  const workspace = useWorkspace();

  const { data: campaignsCount } = useGetCampaignsCountV2Query({ workspaceId: workspace.id });
  const { templatesCount, isLoading: isTemplatesLoading } = useWhatsappTemplates();

  const tabItems = useMemo<TabItem[]>(
    () => [
      {
        id: 'campaigns',
        title: 'campaign:whatsappWorkspace.campaigns' as const,
        icon: <FaBullhorn className='size-4' />,
        count: campaignsCount ?? 'N/A',
        element: <CampaignsWhatsappRoutesV2 />,
      },
      {
        id: 'templates',
        title: 'campaign:whatsappWorkspace.templates' as const,
        icon: <FaRegFileAlt className='size-4' />,
        count: isTemplatesLoading ? 'N/A' : templatesCount,
        element: <CampaignsWhatsappRoutesV2 />,
      },
      {
        id: 'packages',
        title: 'campaign:whatsappWorkspace.packages' as const,
        icon: <FaCreditCard className='size-4' />,
        count: 4,
        element: <CampaignsWhatsappRoutesV2 />,
      },
    ],
    [campaignsCount, isTemplatesLoading, templatesCount]
  );

  const activeTabId = useMemo(() => {
    const isCampaignRoute = WHATSAPP_CAMPAIGNS_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isCampaignRoute) {
      return 'campaigns';
    }

    const isTemplateRoute = WHATSAPP_TEMPLATES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isTemplateRoute) {
      return 'templates';
    }

    const isPackageRoute = WHATSAPP_PACKAGES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isPackageRoute) {
      return 'packages';
    }

    return 'campaigns';
  }, [location.pathname]);

  const currentTab = tabItems.find((item) => item.id === activeTabId) ?? tabItems[0];

  const handleSelectTab = (tabItem: TabItem) => {
    if (tabItem.id === 'campaigns') {
      history.push(RouteConstants.whatsappCampaigns);
      return;
    }

    if (tabItem.id === 'templates') {
      history.push(RouteConstants.whatsappTemplates);
      return;
    }

    if (tabItem.id === 'packages') {
      history.push(RouteConstants.whatsappPackages);
    }
  };

  return <Tabs items={tabItems} currentTab={currentTab.title} onSelectTab={handleSelectTab} />;
};

export const CampaignsWhatsappWorkspaceV2: FC = () => {
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();

  // Query connected connectors
  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );

  const isWhatsappAuthenticated = customConnectors?.find((connector) => connector.name === WHATSAPP_CONNECTOR_NAME)?.isAuthenticated ?? false;

  if (isConnectorsLoading) {
    return <Loading />;
  }

  if (!isWhatsappAuthenticated) {
    return <ConnectorNotConnectedBlock type='whatsapp' />;
  }

  return <WhatsappWorkspaceInit />;
};
